import { useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "id"> {
  label: string;
  hint?: ReactNode;
}

export function Select({ label, hint, className, children, ...props }: SelectProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="type-label text-ink">
        {label}
      </label>
      <select
        id={id}
        aria-describedby={hint ? `${id}-hint` : undefined}
        className={cn(
          "w-full control-lg rounded-element bg-card text-ink type-body",
          "border border-border px-3 hover:border-border-emphasized focus:border-accent",
          "transition-[border-color] motion-fast",
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {hint ? (
        <p id={`${id}-hint`} className="type-support text-ink-secondary">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

interface ToggleProps {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** A setting that cannot take effect right now. The stored value is kept —
   *  this only stops it being changed while it would mean nothing. */
  disabled?: boolean;
}

export function Toggle({ label, hint, checked, onChange, disabled }: ToggleProps) {
  const id = useId();
  return (
    <div className={cn("flex items-start gap-3", disabled && "opacity-60")}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 size-4 shrink-0 rounded-inner accent-accent disabled:cursor-not-allowed"
      />
      <div className="min-w-0">
        <label htmlFor={id} className="type-label text-ink">
          {label}
        </label>
        {hint ? <p className="type-support text-ink-secondary mt-0.5">{hint}</p> : null}
      </div>
    </div>
  );
}
